import { inject } from '@angular/core';
import { provideNamedApollo } from 'apollo-angular';
import { createPersistedQueryLink } from 'apollo-angular/persisted-queries';
import { HttpLink } from 'apollo-angular/http';
import { ErrorHandlerService } from '@services/error/error.service';
import { environment } from '@/environments/environment';
import { onError } from '@apollo/client/link/error';
import { defaultDataIdFromObject, InMemoryCache } from '@apollo/client/cache';
import { uniqBy } from 'lodash-es';
import { ApolloLink } from '@apollo/client/core';
import { DebugService } from '@core/services/debug/debug.service';
import { HttpHeaders } from '@angular/common/http';

const HEADER_INIT = new HttpHeaders();
HEADER_INIT.append('Content-Type', 'application/json');
HEADER_INIT.append('Apollo-Require-Preflight', 'true');

export const provideApollo = () => {
  return provideNamedApollo(() => {
    const httpLink = inject(HttpLink);
    const errorHandlerService = inject(ErrorHandlerService);
    const debugService = inject(DebugService);

    const isBradesco = window.location.hostname.includes('bradesco');

    let persistedQueriesLink: ApolloLink;
    const isPersistedQueriesEnabled =
      debugService.getSingleDebugMetadata<boolean>('persistedQueriesEnabled');
    if (
      window.isSecureContext &&
      environment.ENVIRONMENT !== 'development' &&
      isPersistedQueriesEnabled !== false
    ) {
      persistedQueriesLink = createPersistedQueryLink({
        sha256: async (query) => {
          const msgUint8 = new TextEncoder().encode(query);
          const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
          return hashHex;
        },
      });
    } else {
      persistedQueriesLink = new ApolloLink((operation, forward) => forward(operation));
    }

    const http = httpLink.create({
      uri: isBradesco ? environment.API_BRADESCO_URL : environment.ISENGARD_URL,
      headers: HEADER_INIT,
    });

    const cachedhttp =
      environment.ENVIRONMENT === 'development' || window.location.hostname === 'localhost'
        ? http
        : httpLink.create({
            uri: new URL('/api/graphql', window.location.origin).toString(),
            useGETForQueries: true,
            headers: HEADER_INIT,
          });

    const errorLink = onError((data) => {
      errorHandlerService.errorEmitter$.next(data);
    });

    const link = persistedQueriesLink.concat(errorLink).concat(http);

    const cachedLink = errorLink.concat(cachedhttp);

    return {
      default: {
        cache: new InMemoryCache({
          dataIdFromObject: (object) => {
            switch (object.__typename) {
              case 'PaginationByOffsetUnopenedCards':
                return `${object.__typename}:${object['suid']}`;
              default:
                return defaultDataIdFromObject(object);
            }
          },
          typePolicies: {
            PaginationByOffsetUnopenedCards: {
              fields: {
                items: {
                  merge(existing = [], incoming) {
                    const items = uniqBy([...existing, ...incoming], '__ref');

                    return items;
                  },
                },
              },
            },
            Stage: {
              merge(existing, incoming) {
                return Object.assign({}, existing, incoming);
              },
            },
          },
        }),
        link,
        defaultOptions: {
          query: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
          },
        },
      },
      cached: {
        cache: new InMemoryCache(),
        link: cachedLink,
        defaultOptions: {
          query: {
            fetchPolicy: 'cache-first',
            errorPolicy: 'all',
          },
        },
      },
    };
  });
};
